import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

const navLinks = [
  { to: "/", label: "首页", end: true },
  { to: "/news", label: "最新资讯", end: false },
  { to: "/about", label: "关于爱妻艺", end: false },
];

export default function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const categoryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(event.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = keyword.trim();
    if (!trimmed) return;
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    setKeyword("");
    setMobileOpen(false);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `relative px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
      isActive ? "text-primary-600" : "text-foreground-700 hover:text-primary-600"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="hidden bg-primary-700 text-background-100 md:block">
        <div className="mx-auto flex w-full max-w-[1280px] items-center justify-between px-6 py-1.5 text-xs">
          <span>爱妻艺 · 精彩影视作品与娱乐资讯平台</span>
          <span className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-accent-400"></i>
              每日更新最新影视资讯
            </span>
          </span>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-[1280px] items-center justify-between gap-4 px-4 py-3 md:px-6">
        <div className="flex items-center gap-8">
          <Link to="/" className="flex cursor-pointer items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-clapperboard-line text-xl"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="text-xl font-bold tracking-wide text-primary-700" style={{ fontFamily: "var(--font-heading)" }}>
                爱妻艺
              </span>
              <span className="mt-0.5 text-[10px] tracking-[0.2em] text-foreground-400">AIQIYI MEDIA</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {navLinks.slice(0, 1).map((link) => (
              <NavLink key={link.to} to={link.to} end={link.end} className={linkClass}>
                {link.label}
              </NavLink>
            ))}

            <div ref={categoryRef} className="relative" onMouseLeave={() => setCategoryOpen(false)}>
              <button
                type="button"
                onMouseEnter={() => setCategoryOpen(true)}
                onClick={() => setCategoryOpen((prev) => !prev)}
                className={`flex cursor-pointer items-center gap-1 px-3 py-2 text-sm font-medium transition-colors ${
                  location.pathname.startsWith("/category")
                    ? "text-primary-600"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                影视分类
                <i className={`ri-arrow-down-s-line transition-transform ${categoryOpen ? "rotate-180" : ""}`}></i>
              </button>
              {categoryOpen && (
                <div className="absolute left-0 top-full w-52 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1.5 shadow-lg">
                  {categories.map((category) => (
                    <Link
                      key={category.key}
                      to={category.path}
                      className="flex cursor-pointer items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                    >
                      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                        <i className={category.icon}></i>
                      </span>
                      {category.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {navLinks.slice(1).map((link) => (
              <NavLink key={link.to} to={link.to} end={link.end} className={linkClass}>
                {link.label}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <form onSubmit={handleSearch} className="hidden md:block">
            <div className="flex w-56 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-1.5 transition-colors focus-within:border-primary-400">
              <i className="ri-search-line text-base text-foreground-400"></i>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影视、资讯"
                className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
              />
            </div>
          </form>

          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMobileOpen((prev) => !prev)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <div className="px-4 py-4">
            <form onSubmit={handleSearch} className="mb-4">
              <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2">
                <i className="ri-search-line text-base text-foreground-400"></i>
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索影视、资讯"
                  className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
                />
                <button type="submit" className="cursor-pointer whitespace-nowrap text-sm font-medium text-primary-600">
                  搜索
                </button>
              </div>
            </form>

            <div className="flex flex-col">
              <NavLink
                to="/"
                end
                className="cursor-pointer border-b border-background-200 py-3 text-sm font-medium text-foreground-800"
              >
                首页
              </NavLink>
              <div className="border-b border-background-200 py-3">
                <p className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground-800">
                  <i className="ri-apps-2-line text-primary-600"></i>
                  影视分类
                </p>
                <div className="flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <Link
                      key={category.key}
                      to={category.path}
                      className="flex cursor-pointer items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1.5 text-sm text-primary-700"
                    >
                      <i className={category.icon}></i>
                      {category.name}
                    </Link>
                  ))}
                </div>
              </div>
              {navLinks.slice(1).map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className="cursor-pointer border-b border-background-200 py-3 text-sm font-medium text-foreground-800"
                >
                  {link.label}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}